import React, { useState } from "react";
import { Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import useVendorStore from "../../HomePage/useVendorStore";
import PurchaseOrderDisputeForm from "./PurchaseOrderDisputeForm";
import { formatWeight, PurchaseOrderButton, PurchaseOrderConfirmationModal, PurchaseOrderContactModal, PurchaseOrderFooter, PurchaseOrderHeader, PurchaseOrderProducts, PurchaseOrderSuccessModal, PurchaseOrderSummary } from "./PurchaseOrderDetailsElements";
import styles from "./PurchaseOrderDetails.module.scss";

const PendingPurchaseOrderDetails = ({ purchaseOrder, totalWeight }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);
  const navigate = useNavigate();
  const approvePurchaseOrder = useVendorStore((state) => state.approvePurchaseOrder);
  const updateSellerApprovalStatus = useVendorStore((state) => state.updateSellerApprovalStatus);
  const submitPurchaseOrderDispute = useVendorStore((state) => state.submitPurchaseOrderDispute);
  const submitPurchaseOrderDisputeStatus = useVendorStore((state) => state.submitPurchaseOrderDisputeStatus);
  const submitPurchaseOrderDisputeError = useVendorStore((state) => state.submitPurchaseOrderDisputeErr);
  const items = purchaseOrder.purchase_order_items || [];
  const documentUrl = purchaseOrder.po_document_url?.trim();
  const phoneNumber = String(purchaseOrder.created_by_phone_no || "").trim();

  const downloadPurchaseOrder = () => {
    if (window?.downloadFileMessageHandler) {
      window.downloadFileMessageHandler.postMessage(documentUrl);
      return;
    }
    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };

  const callPurchaseOrderCreator = () => {
    window.location.href = `tel:+91${phoneNumber.replace(/\s/g, "")}`;
  };

  const acceptPurchaseOrder = (successCallback) => {
    approvePurchaseOrder(
      {
        purchase_order_id: purchaseOrder.id,
        vendor_id: parseInt(window.localStorage.getItem("outlet_id"), 10),
      },
      () => {
        setAccepted(true);
        successCallback();
      }
    );
  };

  const submitDispute = (request) => {
    submitPurchaseOrderDispute(request, () => setDisputeSubmitted(true));
  };

  const closeAndReturnToList = () => navigate("/po/list");

  const headerActions = [
    documentUrl && {
      key: "download",
      label: "Download purchase order",
      icon: <Download size={19} />,
      onClick: downloadPurchaseOrder,
    },
    phoneNumber && {
      key: "call",
      label: "Call purchase order creator",
      icon: <img src="/logos/call.png" width="40" height="40" alt="" />,
      onClick: () => setShowContactDetails(true),
    },
  ].filter(Boolean);

  if (showDisputeForm) {
    return (
      <PurchaseOrderDisputeForm
        purchaseOrder={purchaseOrder}
        totalWeight={totalWeight}
        submitting={submitPurchaseOrderDisputeStatus === STATUS_BEGIN}
        failed={submitPurchaseOrderDisputeStatus === STATUS_FAIL}
        submitError={submitPurchaseOrderDisputeError}
        submitted={disputeSubmitted}
        onBack={() => setShowDisputeForm(false)}
        onSubmit={submitDispute}
        onSubmittedClose={closeAndReturnToList}
      />
    );
  }

  return (
    <main className={styles.page}>
      <PurchaseOrderHeader
        number={purchaseOrder.purchase_order_number}
        summary={`${items.length} SKUs • ${formatWeight(totalWeight)}`}
        backPath="/po/list"
        actions={headerActions}
      />
      <div
        className="fs-12 fw-600 text-center"
        style={{
          textAlign: "center",
          padding: "9px 16px",
          borderRadius: "0 0 16px 16px",
          background: "#fff2e8",
          color: "#e65e2e",
          boxShadow: "0 2px 5px rgba(34, 45, 65, 0.06)",
        }}
      >
        Unapproved
      </div>

      <div className={styles.content}>
        <PurchaseOrderSummary purchaseOrder={purchaseOrder} />
        <PurchaseOrderProducts items={items} />
      </div>

      <PurchaseOrderFooter>
        <PurchaseOrderButton type="button" secondary onClick={() => setShowDisputeForm(true)}>
          Raise dispute
        </PurchaseOrderButton>
        <PurchaseOrderButton type="button" onClick={() => setShowConfirmation(true)}>
          Accept PO
        </PurchaseOrderButton>
      </PurchaseOrderFooter>

      <PurchaseOrderConfirmationModal
        visible={showConfirmation && !accepted}
        purchaseOrder={purchaseOrder}
        items={items}
        totalWeight={totalWeight}
        submitting={updateSellerApprovalStatus === STATUS_BEGIN}
        onBack={() => setShowConfirmation(false)}
        onConfirm={() => acceptPurchaseOrder(() => setShowConfirmation(false))}
      />
      <PurchaseOrderSuccessModal
        visible={accepted}
        title="PO Accepted"
        onClose={closeAndReturnToList}
      />
      <PurchaseOrderContactModal
        contact={
          showContactDetails
            ? {
                name: purchaseOrder.created_by_user_name || "PO creator",
                phoneNumber,
              }
            : null
        }
        onClose={() => setShowContactDetails(false)}
        onCall={callPurchaseOrderCreator}
      />
    </main>
  );
};

export default PendingPurchaseOrderDetails;
